import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import { useRecordGrowth } from "../../hooks/useSowingInstructions";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { SowingConfirmBody } from "./SowingConfirmSummary";

interface RecordGrowthDialogProps {
  instruction: SowingInstruction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RecordGrowthDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: RecordGrowthDialogProps) => {
  const [formData, setFormData] = useState({
    actualGrowthDate: new Date().toISOString().split("T")[0],
    quantityGrowing: "",
    quantityGrowingUnitOfMeasure: instruction.unit.toString(),
    notes: "",
  });

  const recordMutation = useRecordGrowth();
  const notify = useNotification();
  const unitsQuery = useUnitsOfMeasure();
  const confirm = useConfirm();

  const handleSubmit = () => {
    if (!formData.actualGrowthDate || !formData.quantityGrowing) {
      notify.error("Please fill in all required fields");
      return;
    }

    confirm({
      title: "Record growth?",
      description: "The growing quantity will be recorded for this instruction.",
      confirmLabel: "Record growth",
      body: SowingConfirmBody(instruction, [
        { label: "Growth date", value: formData.actualGrowthDate },
        { label: "Quantity growing", value: formData.quantityGrowing },
      ]),
      onConfirm: () =>
        recordMutation
          .mutateAsync({
            sowingInstructionId: instruction.id,
            actualGrowthDate: new Date(formData.actualGrowthDate).toISOString(),
            quantityGrowing: parseFloat(formData.quantityGrowing),
            quantityGrowingUnitOfMeasure: parseInt(formData.quantityGrowingUnitOfMeasure),
            notes: formData.notes,
          })
          .then(() => {
            notify.success("Growth recorded successfully");
            onSuccess();
          })
          .catch((error: any) => {
            notify.error(error.response?.data?.message || "Failed to record growth");
            throw error;
          }),
    });
  };

  const trayTypeLabel =
    instruction.pricingMode === 2
      ? "Per Seedling/Piece"
      : instruction.trayTypeName ||
        (instruction.trayType === 210
          ? "210-Hole Tray"
          : instruction.trayType === 442
            ? "442-Hole Tray"
            : null);

  const fmtDate = (d?: string | null) => {
    if (!d) return null;
    const dt = new Date(d);
    return dt.getFullYear() > 1 ? dt.toLocaleDateString() : null;
  };
  const sownDate =
    fmtDate(instruction.actualSowingDate) ??
    fmtDate(instruction.scheduledSowingDate);
  const germinationDate = fmtDate(instruction.germinationStartedDate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Track Growth</DialogTitle>
          <DialogDescription>
            {instruction.instructionNumber} — {instruction.quantityToSow} units to grow
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
            {instruction.cropTypeName && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Crop Type
                </p>
                <p className="font-medium">{instruction.cropTypeName}</p>
              </div>
            )}
            {instruction.variety && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Variety
                </p>
                <p className="font-medium">{instruction.variety}</p>
              </div>
            )}
            {trayTypeLabel && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Tray Type
                </p>
                <p className="font-medium">{trayTypeLabel}</p>
              </div>
            )}
            {sownDate && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {instruction.actualSowingDate ? "Date Sown" : "Scheduled Sowing"}
                </p>
                <p className="font-medium">{sownDate}</p>
              </div>
            )}
            {germinationDate && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Germinated
                </p>
                <p className="font-medium">{germinationDate}</p>
              </div>
            )}
            {typeof instruction.quantityGerminated === "number" && instruction.quantityGerminated > 0 && (
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Germinated Qty
                </p>
                <p className="font-medium">{instruction.quantityGerminated}</p>
              </div>
            )}
          </div>

          <Field>
            <FieldLabel htmlFor="actualGrowthDate">
              Actual Growth Date <span className="text-destructive">*</span>
            </FieldLabel>
            <DatePicker
              id="actualGrowthDate"
              value={formData.actualGrowthDate}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, actualGrowthDate: value }))
              }
              disabled={recordMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="quantityGrowing">
              Quantity Growing <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              id="quantityGrowing"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.quantityGrowing}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  quantityGrowing: e.target.value,
                }))
              }
              disabled={recordMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="unit">Unit</FieldLabel>
            <Select
              value={formData.quantityGrowingUnitOfMeasure}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  quantityGrowingUnitOfMeasure: value,
                }))
              }
              disabled={recordMutation.isPending}
            >
              <SelectTrigger id="unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {unitsQuery.data?.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value.toString()}>
                    {unit.name} ({unit.abbreviation})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="notes">Notes</FieldLabel>
            <Textarea
              id="notes"
              placeholder="Any additional notes..."
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              disabled={recordMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={recordMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={recordMutation.isPending}
            >
              {recordMutation.isPending ? "Recording..." : "Record Growth"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};